// Timeline page — a dropdown over the timelines derived live from this
// knowledge base (statements carrying @validFrom/@validUntil windows). No
// bundled demo content: what renders here came from this store or the page
// says why nothing does.

import { useEffect, useState } from 'react';
import { TimelineView } from '@/components/TimelineView';
import { useTimeline, useTimelineList } from '@/lib/timelines/useTimeline';
import { messageClass } from '@/components/ui/Message';

export function TimelinePage() {
  const listQ = useTimelineList();
  const [id, setId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const first = listQ.data?.[0];
    if (!id && first) setId(first.id);
  }, [id, listQ.data]);

  const specQ = useTimeline(id);
  const spec = specQ.data;
  const empty = listQ.data !== undefined && listQ.data.length === 0;

  return (
    <div className="container mx-auto max-w-[1480px] py-6">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-tres-text-primary">Timeline</h1>
          <p className="text-sm text-tres-text-secondary">
            Derived live from this knowledge base — every span is a statement with a
            validity window.
          </p>
        </div>
        {(listQ.data?.length ?? 0) > 0 && (
          <label className="flex items-center gap-2 text-sm text-tres-text-secondary">
            <span>Timeline</span>
            <select
              value={id ?? ''}
              onChange={(e) => setId(e.target.value)}
              className="rounded-md border border-input bg-card px-2 py-1.5 text-sm text-tres-text-primary"
            >
              {(listQ.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {empty && (
        <div className="rounded-lg border border-dashed border-border bg-card/50 p-8 text-center text-sm text-tres-text-secondary">
          <p className="font-medium text-tres-text-primary">No timelines yet</p>
          <p className="mt-1">
            Timelines are built from statements that carry validity windows
            (<span className="font-mono">@validFrom</span> /{' '}
            <span className="font-mono">@validUntil</span>). None were found in this data.
          </p>
        </div>
      )}

      {spec && (
        <section>
          <div className="mb-2">
            <h2 className="text-lg font-medium text-tres-text-primary">{spec.title}</h2>
            <p className="text-xs text-tres-text-secondary">
              Each row is one statement carrying a validity window (@validFrom /
              @validUntil). An open left edge means the start is unrecorded; a span with
              no right edge is still in force.
            </p>
          </div>
          <TimelineView spec={spec} />
        </section>
      )}

      {specQ.isLoading && id && (
        <div className="rounded-md border border-border bg-card p-6 text-sm text-tres-text-secondary">
          Loading…
        </div>
      )}
      {specQ.error && (
        <div className={messageClass('warning', 'normal')}>
          {(specQ.error as Error).message}
        </div>
      )}
    </div>
  );
}
